import React, { useEffect, useRef, useState } from "react";
import { getAllParameters } from "../../../../server/server";

const Counter = ({ name, value, type, quarter }: any) => {
  let subText = "";
  const key = name;
  let displayName = name;
  const q = quarter ?? "";
  const [npaaFrom, setNpaaFrom] = useState<any[]>([]);

  const getPreviousQuarter = (value: string) => {
    const romans = ["I", "II", "III", "IV"];
    const [year, roman] = value.split("/");
    const index = romans.indexOf(roman);
    if (!year || index === -1) return "";
    return index === 0
      ? `${Number(year) - 1}/IV`
      : `${year}/${romans[index - 1]}`;
  };

  useEffect(() => {
    getAllParameters().then((response: any) => {
      const npaaFrom = response.data.find(
        (p: any) => p.name === "npaa-from",
      )?.value;

      setNpaaFrom(npaaFrom);
    });
  }, []);

  const getColorPallete = (name: string) => {
    switch (name) {
      case "totalOverallCount":
        return {
          bgClass: "bg-[#F0D9FF] border border-[#9932CC] rounded-[8px]",
          numberClass: "text-[#4B0082] text-[32px]",
          mainTextClass: "text-[#374151] text-[14px]",
          subTextClass: "text-[#6B7280] text-[12px]",
        };
      case "totalPlannedCount":
        return {
          bgClass: "bg-[#F0F9FF] border border-[#BFDBFE] rounded-[8px]",
          numberClass: "text-[#1D4ED8] text-[32px]",
          mainTextClass: "text-[#374151] text-[14px]",
          subTextClass: "text-[#6B7280] text-[12px]",
        };
      case "realisedCount":
        return {
          bgClass: "bg-[#F0FDF4] border border-[#BBF7D0] rounded-[8px]",
          numberClass: "text-[#166534] text-[32px]",
          mainTextClass: "text-[#374151] text-[14px]",
          subTextClass: "text-[#6B7280] text-[12px]",
        };
      case "previousQuarterUnrealisedCount":
        return {
          bgClass: "bg-[#FEF2F2] border border-[#FECACA] rounded-[8px]",
          numberClass: "text-[#DC2626] text-[32px]",
          mainTextClass: "text-[#374151] text-[14px]",
          subTextClass: "text-[#6B7280] text-[12px]",
        };
      case "totalUnrealisedCount":
        return {
          bgClass: "bg-[#FFFBEB] border border-[#FED7AA] rounded-[8px]",
          numberClass: "text-[#EA580C] text-[32px]",
          mainTextClass: "text-[#374151] text-[14px]",
          subTextClass: "text-[#6B7280] text-[12px]",
        };
      default:
        return {
          bgClass: "bg-gray-100",
          numberClass: "text-gray-800",
          mainTextClass: "text-gray-800",
          subTextClass: "text-gray-600",
        };
    }
  };

  switch (key) {
    case "totalOverallCount":
      displayName =
        type === "measure" ? "Укупно планираних мера" : "Укупан број прописа";
      subText =
        type === "measure"
          ? `Планиране за ${q}`
          : `Планирани за ${q} + нереализовани из ранијих квартала`;
      break;
    case "totalPlannedCount":
      displayName =
        type === "measure"
          ? `Укупно планираних мера за ${q}`
          : `Укупан број планираних прописа за ${q}`;
      subText = `Планирани за реализацију у ${q}`;
      break;
    case "realisedCount":
      displayName = "Реализовано";
      subText =
        type === "measure"
          ? `Мере са статусом "Објављен" у ${q}`
          : `Прописи са статусом "Објављен" у ${q}`;
      break;
    case "previousQuarterUnrealisedCount":
      displayName = "Нереализовано од почетка примене НПАА";
      subText = `Од ${npaaFrom} до ${getPreviousQuarter(q)}`;
      break;
    case "totalUnrealisedCount":
      displayName = `Нереализовано у ${q}`;
      subText =
        type === "measure"
          ? "Преносе се аутоматски у наредни квартал"
          : `Преносе се аутоматски у наредни квартал`;
      break;
    default:
      break;
  }

  const formatLabel = (k: string = "") =>
    k
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s?.toUpperCase())
      .replace("Count", "")
      .trim();

  const { bgClass, numberClass, mainTextClass, subTextClass } =
    getColorPallete(key);

  const labelToShow = displayName !== key ? displayName : formatLabel(key);
  const prevRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [animatedValue, setAnimatedValue] = useState<number>(0);

  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  useEffect(() => {
    if (typeof value !== "number") {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      prevRef.current = null;
      setAnimatedValue(0);
      return;
    }

    const from = typeof prevRef.current === "number" ? prevRef.current : 0;
    const to = value;
    const duration = 700;
    const start = performance.now();

    const step = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      const current = Math.round(from + (to - from) * eased);
      setAnimatedValue(current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        prevRef.current = to;
        rafRef.current = null;
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value]);

  const renderedValue =
    typeof value === "number" ? animatedValue : String(value ?? "-");

  return (
    <div className={`px-4 py-3 flex flex-col ${bgClass}`}>
      <span className={`font-semibold ${numberClass}`}>{renderedValue}</span>
      <span className={`${mainTextClass}`}>{labelToShow}</span>

      <span className={`${subTextClass}`}>{subText}</span>
    </div>
  );
};

export default Counter;
